import { useLayoutEffect, useRef } from 'react'
import type { JSX } from 'react'
import type { RefObject } from 'react'
import * as m from 'motion/react-m'

import styles from './EngagementInsertedTextHighlight.module.css'

/** A `[start, end)` slice of the textarea text to highlight. */
export type InsertedRange = { start: number; end: number }

type EngagementInsertedTextHighlightProps = {
  /** The textarea whose text is being mirrored. Scroll position is read from here. */
  textareaRef: RefObject<HTMLTextAreaElement>
  /** The current full text of the textarea. */
  text: string
  /** The slice of `text` to paint the rotating gradient on. */
  range: InsertedRange
  /** Fires when the gradient animation completes. */
  onComplete?: () => void
}

// `background-clip: text` cannot be applied to a <textarea> directly (its
// glyphs are rendered by the native form-control layer with no DOM substring
// to clip against), so the gradient lives on this overlay span which mirrors
// the textarea text.
const GRADIENT_KEYFRAMES = [
  'linear-gradient(0deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(45deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(90deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(135deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(180deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(225deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(270deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(315deg, #567efa 0%, #fb98d5 100%)',
  'linear-gradient(360deg, #567efa 0%, #fb98d5 100%)',
]

/**
 * Renders an absolute-positioned overlay over an engagement reply textarea
 * that paints a rotating gradient on only the freshly inserted slice (`range`)
 * while leaving `before` / `after` text in the neutral colour.
 *
 * Long-content correctness: tracks the textarea's scroll position via a
 * `transform: translate()` on the overlay's inner content. The sync runs on
 * every textarea scroll event AND once on mount / range change — the
 * textarea may auto-scroll to keep the cursor visible after a value change,
 * and `scroll` doesn't fire on programmatic value changes.
 *
 * The textarea's text is expected to be transparent while this overlay is
 * rendered (see `.highlighting` modifier on the consumer side); painting the
 * same glyphs in both the form-control glyph layer and the overlay div
 * produces visible "double text" due to sub-pixel hinting differences
 * between the two rendering paths.
 */
export const EngagementInsertedTextHighlight = ({
  textareaRef,
  text,
  range,
  onComplete,
}: EngagementInsertedTextHighlightProps): JSX.Element | null => {
  const overlayContentRef = useRef<HTMLDivElement>(null)

  // Before paint: scroll the textarea so the freshly inserted tail is on-
  // screen, sync the overlay's transform to that scroll position, and start
  // tracking subsequent scrolls. `useLayoutEffect` so frame 1 shows the
  // gradient aligned with the underlying glyphs — `scroll` events don't fire
  // on programmatic value changes, so the listener alone would miss the
  // initial state.
  useLayoutEffect(() => {
    const ta = textareaRef.current
    if (!ta) return

    ta.scrollTop = ta.scrollHeight

    const sync = (): void => {
      const o = overlayContentRef.current
      if (!o) return
      o.style.transform = `translate(${-ta.scrollLeft}px, ${-ta.scrollTop}px)`
    }

    sync()
    ta.addEventListener('scroll', sync, { passive: true })
    return (): void => {
      ta.removeEventListener('scroll', sync)
    }
  }, [textareaRef, text, range])

  if (range.end > text.length || range.start >= range.end) {
    return null
  }

  const before = text.slice(0, range.start)
  const inserted = text.slice(range.start, range.end)
  const after = text.slice(range.end)

  return (
    <div className={styles.overlay} aria-hidden="true">
      <div ref={overlayContentRef} className={styles.content}>
        <span className={styles.plainSegment}>{before}</span>
        <m.span
          key={`${range.start}-${range.end}`}
          className={styles.gradientSegment}
          initial={{ backgroundImage: GRADIENT_KEYFRAMES[0] }}
          animate={{ backgroundImage: GRADIENT_KEYFRAMES }}
          transition={{
            duration: 2,
            ease: [0.4, 0, 0.2, 1],
            times: [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1],
          }}
          onAnimationComplete={() => onComplete?.()}
        >
          {inserted}
        </m.span>
        <span className={styles.plainSegment}>{after}</span>
      </div>
    </div>
  )
}

EngagementInsertedTextHighlight.displayName = 'EngagementInsertedTextHighlight'
